/* ================================================================
   Inspector Panel - Right side accordion panel showing project
   tree and selected layer properties. Light Tailwind theme.
   ================================================================ */

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  ChevronDown, Layers, Info, Palette, Pencil,
  ClipboardList, ShieldCheck, AlertTriangle, AlertCircle, CheckCircle2,
  Plus,
} from 'lucide-react';
import {
  useEditorStore, getBomSummary, getValidationReport,
} from '../state/editorStore';
import type { LayerStyle, RenderNode, SideId, ValidationIssue } from '../core/model/types';
import { getSideLabel } from '../core/renderTree';
import { styleLabel, defaultBlockBoard, defaultThickness } from '../core/model/defaults';
import { getMaterialLibrary, resolveMaterial } from '../core/materials';

// --------------- Accordion ---------------

function AccordionSection({
  title, icon: Icon, defaultOpen = false, children,
}: {
  title: string;
  icon?: React.ComponentType<{ className?: string }>;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-birch-200">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-semibold text-walnut-900 hover:bg-pine-50"
      >
        <span className="flex items-center gap-2">
          {Icon ? <Icon className="h-4 w-4 text-wood-500" /> : null}
          {title}
        </span>
        <ChevronDown
          className={`h-4 w-4 text-oak-300 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open ? <div className="px-4 pb-4">{children}</div> : null}
    </div>
  );
}

function PropRow({
  label, value, children,
}: {
  label: string;
  value?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-2 py-1 text-[12px]">
      <span className="text-oak-500">{label}</span>
      <span className="flex items-center gap-1 text-walnut-900">
        {children}
        {value}
      </span>
    </div>
  );
}

// --------------- Project Info ---------------

function ProjectInfo() {
  const project = useEditorStore(s => s.project);
  return (
    <div>
      <PropRow label="模式" value={project.meta.mode === 'pallet' ? '托盘' : '木箱'} />
      <PropRow
        label="内尺寸"
        value={`${project.innerSize.length}×${project.innerSize.width}×${project.innerSize.height}`}
      />
      <PropRow
        label="外尺寸"
        value={`${project.outerSize.length}×${project.outerSize.width}×${project.outerSize.height}`}
      />
      <PropRow label="载重" value={`${project.payloadKg} kg`} />
    </div>
  );
}

// --------------- Structure Tree ---------------

function ProjectTree() {
  const project = useEditorStore(s => s.project);
  const selectedMeshIds = useEditorStore(s => s.selectedMeshIds);
  const renderNodes = useEditorStore(s => s.renderNodes);
  const selectMesh = useEditorStore(s => s.selectMesh);
  const showContextMenu = useEditorStore(s => s.showContextMenu);
  const focusedSideId = useEditorStore(s => s.focusedSideId);
  const setFocusedSide = useEditorStore(s => s.setFocusedSide);
  const addLayer = useEditorStore(s => s.addLayer);

  const selectedLayers = useMemo(() => {
    const set = new Set<string>();
    for (const node of renderNodes) {
      if (selectedMeshIds[node.meshId]) {
        set.add(`${node.sideId}-${node.layerIndex}`);
      }
    }
    return set;
  }, [renderNodes, selectedMeshIds]);

  const firstMeshBySide = useMemo(() => {
    const map = new Map<string, string>();
    for (const node of renderNodes) {
      const key = `${node.sideId}-${node.layerIndex}`;
      if (!map.has(key)) map.set(key, node.meshId);
    }
    return map;
  }, [renderNodes]);

  return (
    <div className="space-y-2">
      {project.sides.map(side => {
        const isFocused = focusedSideId === side.sideId;
        return (
          <div key={side.sideId} className="rounded-md border border-birch-200 bg-pine-50/60">
            <div
              className={`flex items-center rounded-md ${
                isFocused ? 'bg-wood-100' : ''
              } ${side.enabled ? '' : 'opacity-50'}`}
            >
              <button
                onClick={() => setFocusedSide(isFocused ? null : side.sideId)}
                className={`flex flex-1 items-center justify-between px-3 py-2 text-left text-xs font-medium transition-colors ${
                  isFocused ? 'text-wood-700' : 'text-walnut-700'
                }`}
              >
                <span>{getSideLabel(side.sideId)}</span>
                <span className="text-[10px] text-oak-300">{side.layers.length} 层</span>
              </button>
              {side.enabled ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    addLayer(side.sideId, side.layers.length, 0, 0);
                  }}
                  title={`在 ${getSideLabel(side.sideId)} 添加一层`}
                  className="mr-1 flex h-6 w-6 items-center justify-center rounded-md text-oak-500 hover:bg-wood-100 hover:text-wood-700"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              ) : null}
            </div>
            {side.enabled ? (
              <div className="space-y-0.5 border-t border-birch-200 px-2 py-1.5">
                {side.layers.map((layer, li) => {
                  const key = `${side.sideId}-${li}` as const;
                  const active = selectedLayers.has(key);
                  const firstMesh = firstMeshBySide.get(key);
                  return (
                    <button
                      key={layer.id}
                      onClick={() => { if (firstMesh) selectMesh(firstMesh, false); }}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (firstMesh) {
                          selectMesh(firstMesh, false);
                        }
                        const root = e.currentTarget.closest('[data-designer-context-root="true"]') as HTMLElement | null;
                        const rect = (root ?? e.currentTarget).getBoundingClientRect();
                        showContextMenu(e.clientX - rect.left, e.clientY - rect.top);
                      }}
                      className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12px] transition-colors ${
                        active
                          ? 'bg-wood-100 text-wood-700'
                          : 'text-walnut-700 hover:bg-white'
                      }`}
                    >
                      <span
                        className="h-3 w-3 flex-shrink-0 rounded-sm border border-birch-200"
                        style={{ background: layer.color || '#c8a96e' }}
                      />
                      <span className="flex-1 truncate">{layer.name}</span>
                      <span className="text-[10px] text-oak-300">{styleLabel(layer.style)}</span>
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

// --------------- Edit Properties (layer + per-element) ---------------

function EditProperties() {
  const renderNodes = useEditorStore(s => s.renderNodes);
  const selectedMeshIdsForProps = useEditorStore(s => s.selectedMeshIds);
  const selectedNodes = useMemo(
    () => renderNodes.filter(n => selectedMeshIdsForProps[n.meshId]),
    [renderNodes, selectedMeshIdsForProps],
  );
  const changeLayer = useEditorStore(s => s.changeLayer);
  const batchChangeLayer = useEditorStore(s => s.batchChangeLayer);
  const changeElement = useEditorStore(s => s.changeElement);

  const targets = useMemo(() => {
    const map = new Map<string, { sideId: SideId; layerIndex: number; node: RenderNode }>();
    for (const node of selectedNodes) {
      const key = `${node.sideId}-${node.layerIndex}`;
      if (!map.has(key)) {
        map.set(key, { sideId: node.sideId, layerIndex: node.layerIndex, node });
      }
    }
    return [...map.values()];
  }, [selectedNodes]);

  const isMulti = targets.length > 1;

  const project = useEditorStore(s => s.project);
  const firstTarget = targets[0];
  const firstLayer = firstTarget
    ? project.sides.find(s => s.sideId === firstTarget.sideId)?.layers[firstTarget.layerIndex]
    : null;

  const [thickness, setThickness] = useState(firstLayer?.thickness ?? 18);
  const [direction, setDirection] = useState<0 | 1>(firstLayer?.direction ?? 0);
  const [style, setStyle] = useState<LayerStyle>(firstLayer?.style ?? 0);
  const [bbLength, setBbLength] = useState(firstLayer?.blockBoard?.boardLength ?? 0);
  const [bbWidth, setBbWidth] = useState(firstLayer?.blockBoard?.boardWidth ?? 0);
  const [bbCount, setBbCount] = useState(
    firstLayer?.blockBoard?.count || firstLayer?.styleSetting?.count?.board || 3,
  );
  const [bbSpacing, setBbSpacing] = useState(firstLayer?.blockBoard?.centerSpacing ?? 0);
  const [frameCountX, setFrameCountX] = useState(firstLayer?.styleSetting?.count?.frame?.[0] || 3);
  const [frameCountZ, setFrameCountZ] = useState(firstLayer?.styleSetting?.count?.frame?.[1] || 3);
  const [crossBoardCount, setCrossBoardCount] = useState(firstLayer?.styleSetting?.count?.board || 0);
  const [crossBraceCount, setCrossBraceCount] = useState(firstLayer?.styleSetting?.count?.horBrace?.[0] || 0);

  // Per-element (板条/木方) — form shared for 1..N selected nodes; 应用 broadcasts.
  const computeNodeDims = useCallback((node: RenderNode) => {
    const { width: gw, height: gh, depth: gd } = node.geometry;
    let a: number, b: number, t: number;
    switch (node.sideId) {
      case 'pedestal': case 'topCap': a = gw; b = gd; t = gh; break;
      case 'sideFront': case 'sideBack': a = gw; b = gh; t = gd; break;
      default: a = gd; b = gh; t = gw; break;
    }
    return a >= b
      ? { length: Math.round(a), width: Math.round(b), thickness: Math.round(t) }
      : { length: Math.round(b), width: Math.round(a), thickness: Math.round(t) };
  }, []);
  const elementDims = useMemo(() => {
    if (selectedNodes.length === 0) return { length: 0, width: 0, thickness: 0, mixed: false };
    const list = selectedNodes.map(computeNodeDims);
    const first = list[0];
    const uniform = list.every(d =>
      d.length === first.length && d.width === first.width && d.thickness === first.thickness,
    );
    return { ...first, mixed: !uniform };
  }, [selectedNodes, computeNodeDims]);
  const [elLen, setElLen] = useState(elementDims.length);
  const [elWid, setElWid] = useState(elementDims.width);
  const [elThk, setElThk] = useState(elementDims.thickness);
  useEffect(() => {
    setElLen(elementDims.length);
    setElWid(elementDims.width);
    setElThk(elementDims.thickness);
  }, [elementDims.length, elementDims.width, elementDims.thickness]);

  // Parent <Inspector /> remounts this component on layer change via `key`,
  // so state is always fresh. We only keep a baseline snapshot so the top
  // 应用 button can detect which fields the user actually touched
  // (changeLayer wipes elementOverrides — avoid calling it when untouched).
  const initialLayerRef = useRef({
    thickness, direction, style, bbLength, bbWidth, bbCount, bbSpacing,
    frameCountX, frameCountZ, crossBoardCount, crossBraceCount,
  });

  const handleStyleChange = useCallback((nextStyle: LayerStyle) => {
    setStyle(nextStyle);
    setThickness(defaultThickness(nextStyle));
    if (nextStyle === 0 || nextStyle === 4) {
      const defaults = defaultBlockBoard(nextStyle);
      setBbLength(defaults.boardLength);
      setBbWidth(defaults.boardWidth);
      setBbCount(defaults.count);
      setBbSpacing(defaults.centerSpacing ?? 0);
      setFrameCountX(nextStyle === 4 ? 3 : 0);
      setFrameCountZ(nextStyle === 4 ? 3 : 0);
      setCrossBoardCount(0);
      setCrossBraceCount(0);
    } else if (nextStyle === 3) {
      setBbLength(0);
      setBbWidth(0);
      setBbCount(0);
      setBbSpacing(0);
      setFrameCountX(0);
      setFrameCountZ(0);
      setCrossBoardCount(1);
      setCrossBraceCount(1);
    } else {
      setBbLength(0);
      setBbWidth(0);
      setBbCount(0);
      setBbSpacing(0);
      setFrameCountX(0);
      setFrameCountZ(0);
      setCrossBoardCount(0);
      setCrossBraceCount(0);
    }
  }, []);

  const handleApply = useCallback(() => {
    const init = initialLayerRef.current;
    const layerDirty =
      thickness !== init.thickness ||
      direction !== init.direction ||
      style !== init.style ||
      bbLength !== init.bbLength ||
      bbWidth !== init.bbWidth ||
      bbCount !== init.bbCount ||
      bbSpacing !== init.bbSpacing ||
      frameCountX !== init.frameCountX ||
      frameCountZ !== init.frameCountZ ||
      crossBoardCount !== init.crossBoardCount ||
      crossBraceCount !== init.crossBraceCount;

    if (layerDirty) {
      const patch: Record<string, unknown> = { thickness, direction, style };
      if (style !== 4) {
        patch.blockBoard = { boardLength: bbLength, boardWidth: bbWidth };
      }
      if (style === 0) {
        patch.blockBoard = {
          boardLength: bbLength,
          boardWidth: bbWidth,
          count: bbCount,
          centerSpacing: bbSpacing,
        };
        patch.countBoard = bbCount;
      }
      if (style === 3) {
        patch.countBoard = crossBoardCount;
        patch.countHorBrace = [crossBraceCount];
      }
      if (style === 4) {
        patch.blockBoard = { boardLength: bbLength, boardWidth: bbWidth };
        patch.countFrame = [frameCountX, frameCountZ];
      }
      if (isMulti) {
        batchChangeLayer(
          targets.map(t => ({ sideId: t.sideId, layerIndex: t.layerIndex })),
          patch as any,
        );
      } else if (firstTarget) {
        changeLayer(firstTarget.sideId, firstTarget.layerIndex, patch as any);
      }
    }

    const elementDirty =
      (style === 0 || style === 4) &&
      selectedNodes.length > 0 &&
      (elLen !== elementDims.length || elWid !== elementDims.width || elThk !== elementDims.thickness);

    if (elementDirty) {
      for (const node of selectedNodes) {
        changeElement(node.sideId, node.layerIndex, node.elementIndex, {
          length: elLen, width: elWid, thickness: elThk,
        });
      }
    }

    initialLayerRef.current = {
      thickness, direction, style, bbLength, bbWidth, bbCount, bbSpacing,
      frameCountX, frameCountZ, crossBoardCount, crossBraceCount,
    };
  }, [thickness, direction, style, bbLength, bbWidth, bbCount, bbSpacing, frameCountX, frameCountZ, crossBoardCount, crossBraceCount, isMulti, targets, firstTarget, changeLayer, batchChangeLayer, changeElement, selectedNodes, elLen, elWid, elThk, elementDims.length, elementDims.width, elementDims.thickness]);

  if (!firstLayer) {
    return (
      <div className="rounded-md border border-dashed border-birch-200 p-4 text-center text-[12px] text-oak-300">
        点击结构树或 3D 视图中的构件开始编辑
      </div>
    );
  }

  const inputClass = 'w-full rounded-md border border-birch-200 bg-white px-2 py-1 text-[12px] focus:border-wood-500 focus:outline-none';
  const labelClass = 'block text-[10px] text-oak-500';

  return (
    <div className="text-[11px]">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="text-[10px] font-semibold text-oak-500">
          {isMulti ? `批量编辑 (${targets.length} 层)` : `${getSideLabel(firstTarget!.sideId)} / 第 ${firstTarget!.layerIndex + 1} 层`}
        </div>
        <button
          onClick={handleApply}
          className="rounded-md bg-wood-500 px-3 py-1 text-[11px] font-medium text-white hover:bg-wood-700"
        >
          {isMulti ? '全部应用' : '应用'}
        </button>
      </div>

      <div className="space-y-2">
        <div className="rounded-md border border-birch-200 bg-pine-50/60 p-2">
          <div className="mb-2 text-[10px] font-semibold text-oak-500">基础设置</div>
          <div className="grid grid-cols-2 gap-2">
            <label>
              <span className={labelClass}>类型</span>
              <select value={style} onChange={e => handleStyleChange(Number(e.target.value) as LayerStyle)} className={inputClass}>
                <option value={0}>板条</option>
                <option value={1}>整板</option>
                <option value={3}>田字</option>
                <option value={4}>木方</option>
              </select>
            </label>
            <label>
              <span className={labelClass}>方向</span>
              <select value={direction} onChange={e => setDirection(Number(e.target.value) as 0 | 1)} className={inputClass}>
                <option value={0}>纵向</option>
                <option value={1}>横向</option>
              </select>
            </label>
          </div>
        </div>

        <div className="rounded-md border border-birch-200 bg-pine-50/60 p-2">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[10px] font-semibold text-oak-500">尺寸参数</span>
            {(style === 0 || style === 4) && selectedNodes.length > 0
              ? <span className="text-[10px] text-oak-300">整层</span>
              : null}
          </div>
          <div className="grid items-start gap-2 grid-cols-3">
            <label className="block self-start">
              <span className={labelClass}>长</span>
              <input
                type="number"
                min={0}
                max={10000}
                value={style === 4 ? (bbLength || 90) : bbLength}
                onChange={e => setBbLength(Number(e.target.value))}
                className={inputClass}
              />
            </label>
            <label className="block self-start">
              <span className={labelClass}>宽</span>
              <input
                type="number"
                min={0}
                max={1000}
                value={style === 4 ? (bbWidth || 90) : bbWidth}
                onChange={e => setBbWidth(Number(e.target.value))}
                className={inputClass}
              />
            </label>
            <label className="block self-start">
              <span className={labelClass}>高(厚)</span>
              <input
                type="number"
                min={1}
                max={500}
                value={thickness}
                onChange={e => setThickness(Number(e.target.value))}
                className={inputClass}
              />
            </label>
          </div>
          <div className="mt-2 text-[10px] text-oak-300">
            {style === 4
              ? '木方长度、宽度与厚度统一在这里设置，阵列数量单独放在下方。'
              : style === 0
                ? '长度为 0 时按当前面的可用尺寸自动计算。'
                : '当前构件的长度、宽度、厚度统一在这一组中调整。'}
          </div>

          {(style === 0 || style === 4) && selectedNodes.length > 0 ? (
            <div className="mt-2 border-t border-birch-200/80 pt-2">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-[10px] font-semibold text-oak-500">
                  当前构件{selectedNodes.length > 1 ? ` · 选中 ${selectedNodes.length} 根` : ''}
                </span>
                <span className="text-[10px] text-oak-300">
                  {selectedNodes.length > 1 ? '批量应用' : '仅此根'}
                </span>
              </div>
              {elementDims.mixed ? (
                <div className="mb-1 rounded-md bg-warning-soft px-1.5 py-0.5 text-[10px] text-warning">
                  选中构件尺寸不一致，填入后将统一应用。
                </div>
              ) : null}
              <div className="grid items-start gap-2 grid-cols-3">
                <label className="block self-start">
                  <span className={labelClass}>长</span>
                  <input
                    type="number"
                    min={1}
                    value={elLen}
                    onChange={e => setElLen(Number(e.target.value))}
                    className={inputClass}
                  />
                </label>
                <label className="block self-start">
                  <span className={labelClass}>宽</span>
                  <input
                    type="number"
                    min={1}
                    value={elWid}
                    onChange={e => setElWid(Number(e.target.value))}
                    className={inputClass}
                  />
                </label>
                <label className="block self-start">
                  <span className={labelClass}>高(厚)</span>
                  <input
                    type="number"
                    min={1}
                    value={elThk}
                    onChange={e => setElThk(Number(e.target.value))}
                    className={inputClass}
                  />
                </label>
              </div>
              <div className="mt-1 text-[10px] text-oak-300">
                {selectedNodes.length > 1
                  ? '将选中的所有构件设为同一尺寸；不影响未选中构件。'
                  : '只修改选中的这一根，不影响同层其他构件。'}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {style === 0 && (
        <div className="mt-3 space-y-2">
          <div className="rounded-md border border-birch-200 bg-pine-50/60 p-2">
            <div className="mb-2 text-[10px] font-semibold text-oak-500">数量与间距</div>
            <div className="grid grid-cols-2 gap-2">
              <label>
                <span className={labelClass}>数量</span>
                <input type="number" min={1} max={50} value={bbCount} onChange={e => setBbCount(Number(e.target.value))} className={inputClass} />
              </label>
              <label>
                <span className={labelClass}>间距</span>
                <input type="number" min={0} max={10000} value={bbSpacing} onChange={e => setBbSpacing(Number(e.target.value))} className={inputClass} />
              </label>
            </div>
            <div className="mt-2 text-[10px] text-oak-300">
              间距为 0 时自动均分；大于 0 时按指定间距排布。
            </div>
          </div>
        </div>
      )}

      {style === 3 && (
        <div className="mt-3 space-y-2">
          <div className="rounded-md border border-birch-200 bg-pine-50/60 p-2">
            <div className="mb-2 text-[10px] font-semibold text-oak-500">田字参数</div>
            <div className="grid grid-cols-2 gap-2">
              <label>
                <span className={labelClass}>竖撑数量</span>
                <input type="number" min={0} max={20} value={crossBoardCount} onChange={e => setCrossBoardCount(Number(e.target.value))} className={inputClass} />
              </label>
              <label>
                <span className={labelClass}>横撑数量(每格)</span>
                <input type="number" min={0} max={20} value={crossBraceCount} onChange={e => setCrossBraceCount(Number(e.target.value))} className={inputClass} />
              </label>
            </div>
            <div className="mt-2 text-[10px] text-oak-300">
              竖撑在两竖边之间均布，横撑在每个竖格内均布。
            </div>
          </div>
        </div>
      )}

      {style === 4 && (
        <div className="mt-3 space-y-2">
          <div className="rounded-md border border-birch-200 bg-pine-50/60 p-2">
            <div className="mb-2 text-[10px] font-semibold text-oak-500">阵列数量</div>
            <div className="grid grid-cols-2 gap-2">
              <label>
                <span className={labelClass}>X 方向数量</span>
                <input type="number" min={1} max={50} value={frameCountX} onChange={e => setFrameCountX(Number(e.target.value))} className={inputClass} />
              </label>
              <label>
                <span className={labelClass}>Z 方向数量</span>
                <input type="number" min={1} max={50} value={frameCountZ} onChange={e => setFrameCountZ(Number(e.target.value))} className={inputClass} />
              </label>
            </div>
            <div className="mt-2 text-[10px] text-oak-300">
              木方按 X/Z 两个方向做阵列排布。
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --------------- Material picker ---------------

function MaterialPicker({
  value, onChange, mixed = false,
}: {
  value: number;
  onChange: (key: number) => void;
  mixed?: boolean;
}) {
  const project = useEditorStore(s => s.project);
  const library = useMemo(() => getMaterialLibrary(project), [project]);
  const [open, setOpen] = useState(false);

  const current = library.find(m => m.key === value) ?? library[0];

  return (
    <div className="rounded-md border border-birch-200 bg-pine-50/60 p-2 text-[11px]">
      <div className="mb-1 text-oak-500">
        材质库
        {mixed ? <span className="ml-1 text-[10px] text-warning">（多个值）</span> : null}
      </div>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="flex w-full items-center gap-2 rounded-md border border-birch-200 bg-white px-2 py-1.5 text-left hover:border-wood-500"
      >
        <span
          className="h-8 w-8 flex-shrink-0 rounded border border-birch-200 bg-cover bg-center"
          style={{
            backgroundImage: current.textureUrl ? `url(${current.textureUrl})` : undefined,
            backgroundColor: current.textureUrl ? undefined : current.color,
          }}
        />
        <span className="flex-1 truncate text-[12px] font-medium text-walnut-900">
          {current.name}
        </span>
        <ChevronDown className={`h-3 w-3 text-oak-300 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open ? (
        <div className="mt-1 max-h-64 overflow-y-auto rounded-md border border-birch-200 bg-white p-1 shadow-inner">
          <div className="grid grid-cols-3 gap-1">
            {library.map(m => {
              const active = m.key === value;
              return (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => { onChange(m.key); setOpen(false); }}
                  className={`flex flex-col items-center gap-0.5 rounded p-1 transition-colors ${
                    active ? 'bg-wood-100 ring-1 ring-wood-500' : 'hover:bg-pine-50'
                  }`}
                  title={m.name}
                >
                  <span
                    className="h-10 w-full rounded border border-birch-200 bg-cover bg-center"
                    style={{
                      backgroundImage: m.textureUrl ? `url(${m.textureUrl})` : undefined,
                      backgroundColor: m.textureUrl ? undefined : m.color,
                    }}
                  />
                  <span className="w-full truncate text-center text-[9px] text-walnut-700">
                    {m.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      <div className="mt-1.5 grid grid-cols-2 gap-1 text-[10px] text-oak-500">
        <span>密度 {current.densityKgPerM3} kg/m³</span>
        <span>单价 ¥{current.pricePerM3}/m³</span>
        {current.bendingStrengthMPa ? (
          <span>抗弯 {current.bendingStrengthMPa} MPa</span>
        ) : null}
        <span className="uppercase">{current.kind}</span>
      </div>
    </div>
  );
}

// --------------- Global (project-wide) material + color ---------------

function GlobalMaterialAndColor() {
  const project = useEditorStore(s => s.project);
  const batchChangeLayer = useEditorStore(s => s.batchChangeLayer);

  // All enabled layers — the targets for project-wide application.
  const allTargets = useMemo(() => {
    const list: Array<{ sideId: SideId; layerIndex: number }> = [];
    for (const side of project.sides) {
      if (!side.enabled) continue;
      for (let i = 0; i < side.layers.length; i++) {
        list.push({ sideId: side.sideId, layerIndex: i });
      }
    }
    return list;
  }, [project.sides]);

  // Representative values: first enabled layer's color and materialKey.
  // We show these as the "current" state; user edits propagate to all.
  const firstLayer = useMemo(() => {
    for (const side of project.sides) {
      if (!side.enabled) continue;
      if (side.layers.length > 0) return side.layers[0];
    }
    return null;
  }, [project.sides]);

  // Detect mixed state (different layers have different values).
  const { mixedMaterial, mixedColor } = useMemo(() => {
    let firstMat: number | null = null;
    let firstCol: string | null = null;
    let mixedMat = false;
    let mixedCol = false;
    for (const side of project.sides) {
      if (!side.enabled) continue;
      for (const l of side.layers) {
        if (firstMat === null) firstMat = l.materialKey;
        else if (firstMat !== l.materialKey) mixedMat = true;
        if (firstCol === null) firstCol = l.color;
        else if (firstCol !== l.color) mixedCol = true;
      }
    }
    return { mixedMaterial: mixedMat, mixedColor: mixedCol };
  }, [project.sides]);

  const applyAll = useCallback((patch: any) => {
    if (allTargets.length === 0) return;
    batchChangeLayer(allTargets, patch);
  }, [allTargets, batchChangeLayer]);

  if (!firstLayer) {
    return (
      <div className="rounded-md border border-dashed border-birch-200 p-4 text-center text-[12px] text-oak-300">
        没有启用的面，先启用一个面。
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="text-[11px] text-oak-500">
        以下操作会同时应用到 <span className="font-semibold text-walnut-700">所有启用面</span> 的 {allTargets.length} 个层。
      </div>

      <MaterialPicker
        value={firstLayer.materialKey}
        onChange={key => applyAll({ materialKey: key })}
        mixed={mixedMaterial}
      />

      <div className="rounded-md border border-birch-200 bg-pine-50/60 p-2 text-[11px]">
        <div className="mb-1 text-oak-500">
          颜色 {mixedColor ? <span className="ml-1 text-[10px] text-warning">（多个值）</span> : null}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={firstLayer.color}
            onChange={e => applyAll({ color: e.target.value })}
            className="h-8 w-10 cursor-pointer rounded-md border border-birch-200"
          />
          <span className="text-[11px] text-oak-500">{mixedColor ? '多个值' : firstLayer.color}</span>
          <button
            onClick={() => applyAll({ color: '#c8a96e' })}
            className="ml-auto rounded-md border border-birch-200 px-2 py-1 text-[10px] text-walnut-700 hover:bg-pine-100"
            title="清除颜色，使用材质默认色"
          >
            重置
          </button>
        </div>
      </div>
    </div>
  );
}

// --------------- BOM summary section ---------------

function BomOverview() {
  const bom = useEditorStore(getBomSummary);

  if (bom.lines.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-birch-200 p-4 text-center text-[12px] text-oak-300">
        暂无构件，先在画布上构建结构。
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2 rounded-md bg-pine-50 p-2 text-[10px]">
        <Stat label="体积" value={`${bom.totalVolumeM3.toFixed(3)} m³`} />
        <Stat label="重量" value={`${bom.totalWeightKg.toFixed(1)} kg`} />
        <Stat label="成本" value={`¥${bom.totalCostYuan.toFixed(0)}`} />
      </div>
      <div className="space-y-1">
        {bom.lines.slice(0, 8).map(line => (
          <div
            key={`${line.materialKey}-${line.spec}`}
            className="rounded-md border border-birch-200 px-2 py-1.5 text-[11px]"
          >
            <div className="flex items-center justify-between">
              <span className="font-medium text-walnut-900">{line.materialName}</span>
              <span className="text-oak-300">×{line.count}</span>
            </div>
            <div className="mt-0.5 flex items-center justify-between text-[10px] text-oak-500">
              <span>{line.spec} mm</span>
              <span>¥{line.totalCostYuan.toFixed(0)}</span>
            </div>
          </div>
        ))}
        {bom.lines.length > 8 ? (
          <div className="text-center text-[10px] text-oak-300">
            另有 {bom.lines.length - 8} 行...
          </div>
        ) : null}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-center">
      <div className="text-oak-300">{label}</div>
      <div className="text-[12px] font-semibold text-walnut-900">{value}</div>
    </div>
  );
}

// --------------- Validator report section ---------------

function SeverityIcon({ severity }: { severity: ValidationIssue['severity'] }) {
  if (severity === 'error') return <AlertCircle className="h-4 w-4 text-danger" />;
  if (severity === 'warning') return <AlertTriangle className="h-4 w-4 text-warning" />;
  return <Info className="h-4 w-4 text-info" />;
}

function ValidationPanel() {
  const report = useEditorStore(getValidationReport);
  const setFocusedSide = useEditorStore(s => s.setFocusedSide);

  if (report.issues.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-md bg-success-soft p-3 text-[12px] text-success">
        <CheckCircle2 className="h-4 w-4" />
        所有检查通过，结构合规。
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3 text-[11px]">
        <span className="rounded-full bg-danger-soft px-2 py-0.5 text-danger">
          错误 {report.errorCount}
        </span>
        <span className="rounded-full bg-warning-soft px-2 py-0.5 text-warning">
          警告 {report.warningCount}
        </span>
        <span className="rounded-full bg-info-soft px-2 py-0.5 text-info">
          提示 {report.infoCount}
        </span>
      </div>
      {report.issues.map(issue => (
        <button
          key={issue.id}
          onClick={() => { if (issue.sideId) setFocusedSide(issue.sideId); }}
          className="w-full rounded-md border border-birch-200 bg-pine-50/60 p-2 text-left transition-colors hover:bg-pine-100"
        >
          <div className="flex items-start gap-2">
            <SeverityIcon severity={issue.severity} />
            <div className="min-w-0 flex-1">
              <div className="text-[12px] font-medium text-walnut-900">{issue.title}</div>
              <div className="mt-0.5 text-[11px] text-oak-500">{issue.detail}</div>
              {issue.hint ? (
                <div className="mt-0.5 text-[10px] text-wood-700">👉 {issue.hint}</div>
              ) : null}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

// --------------- Main ---------------

export default function Inspector() {
  const renderNodes = useEditorStore(s => s.renderNodes);
  const selectedMeshIds = useEditorStore(s => s.selectedMeshIds);
  const project = useEditorStore(s => s.project);
  // Remount EditProperties whenever the selected layer's identity changes.
  // meshId is position-encoded ("sideIdx-layerIdx-elemIdx"), so "向下添加层"
  // shifts the old layer up but keeps the same meshId pointing at the new
  // layer. Keying on the layer's own id avoids that ambiguity.
  const editKey = useMemo(() => {
    for (const n of renderNodes) {
      if (!selectedMeshIds[n.meshId]) continue;
      const side = project.sides.find(s => s.sideId === n.sideId);
      const layer = side?.layers[n.layerIndex];
      if (layer?.id) return layer.id;
      return `${n.sideId}-${n.layerIndex}`;
    }
    return 'none';
  }, [renderNodes, selectedMeshIds, project]);

  return (
    <aside className="flex h-full w-full flex-col overflow-y-auto border-l border-birch-200 bg-white">
      <div className="border-b border-birch-200 px-4 py-3">
        <div className="text-xs uppercase tracking-wider text-oak-300">Inspector</div>
        <div className="text-sm font-semibold text-walnut-900">结构与属性</div>
      </div>

      <AccordionSection title="项目信息" icon={Info}>
        <ProjectInfo />
      </AccordionSection>

      <AccordionSection title="结构树" icon={Layers}>
        <ProjectTree />
      </AccordionSection>

      <AccordionSection title="编辑属性" icon={Pencil} defaultOpen>
        <EditProperties key={editKey} />
      </AccordionSection>

      <AccordionSection title="BOM 概览" icon={ClipboardList}>
        <BomOverview />
      </AccordionSection>

      <AccordionSection title="结构校验" icon={ShieldCheck}>
        <ValidationPanel />
      </AccordionSection>

      <AccordionSection title="材质与颜色" icon={Palette}>
        <GlobalMaterialAndColor />
      </AccordionSection>
    </aside>
  );
}
